import { useState } from "react";
import { toast } from "sonner";
import { Download, KeyRound, Loader2, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PASSWORD_HINT } from "@/components/auth/AuthShell";

const Section = ({ icon: Icon, title, children, danger }) => (
  <section className={`rounded-xl border p-4 ${danger ? "border-rose-500/40 bg-rose-500/5" : "border-slate-700/60 bg-slate-800/50"}`}>
    <h3 className={`mb-3 flex items-center gap-2 text-sm font-semibold ${danger ? "text-rose-300" : ""}`}><Icon className="h-4 w-4" /> {title}</h3>
    {children}
  </section>
);

const Field = ({ id, label, ...props }) => (
  <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label><Input id={id} className="h-10 bg-slate-950" {...props} /></div>
);

const ChangePassword = () => {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", f);
      setF({ current_password: "", new_password: "" });
      toast.success("Senha alterada. As outras sessões foram encerradas.");
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <Field id="ac_cur" label="Senha atual" type="password" required value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="account-current-password" />
      <Field id="ac_new" label="Nova senha" type="password" required autoComplete="new-password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="account-new-password" />
      <p className="text-xs text-slate-500">{PASSWORD_HINT}</p>
      <Button type="submit" size="sm" disabled={busy} className="rounded-full" data-testid="account-change-password-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Alterar senha</Button>
    </form>
  );
};

const exportData = async () => {
  try {
    const { data } = await api.get("/account/export");
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `ciclo-certo-dados-${new Date().toISOString().slice(0, 10)}.json` });
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) { toast.error(errorMessage(err)); }
};

const DeleteAccount = ({ company }) => {
  const { logout } = useAuth();
  const [f, setF] = useState({ password: "", confirm_name: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/account/delete", f);
      toast.success("Conta encerrada. Os dados serão apagados em 30 dias; até lá, fale com o suporte para desfazer.");
      logout();
    } catch (err) { toast.error(errorMessage(err)); setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      <p className="text-xs text-slate-400">A assinatura é cancelada, ninguém mais entra na conta e, depois de 30 dias, clientes, serviços e histórico são apagados de vez. Baixe seus dados antes.</p>
      <Field id="ac_del_name" label={`Digite o nome da empresa: ${company}`} required value={f.confirm_name} onChange={(e) => setF({ ...f, confirm_name: e.target.value })} data-testid="account-delete-name" />
      <Field id="ac_del_pw" label="Sua senha" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="account-delete-password" />
      <Button type="submit" size="sm" variant="destructive" disabled={busy} className="rounded-full" data-testid="account-delete-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Excluir conta da empresa</Button>
    </form>
  );
};

export const AccountDialog = ({ open, onClose }) => {
  const { user } = useAuth();
  const owner = user?.tenant_role === "owner" && !user?.platform_role;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-slate-700 bg-slate-900 sm:max-w-lg" data-testid="account-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">Minha conta</DialogTitle>
          <DialogDescription>{user?.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Section icon={KeyRound} title="Alterar senha"><ChangePassword /></Section>
          {user?.tenant_role === "owner" && (
            <Section icon={Download} title="Seus dados">
              <p className="mb-3 text-xs text-slate-400">Baixe uma cópia de clientes, serviços contratados, atendimentos, catálogo e usuários.</p>
              <Button size="sm" variant="outline" onClick={exportData} className="rounded-full border-slate-700 bg-transparent" data-testid="account-export-button"><Download className="mr-1 h-4 w-4" /> Baixar meus dados</Button>
            </Section>
          )}
          {owner && <Section icon={Trash2} title="Excluir conta" danger><DeleteAccount company={user.company_name} /></Section>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
